import React from "react";
import { enableES5, setUseProxies } from "immer";

import { Box, Hide } from "@catchoftheday/cg-components";
import { CustomerContextProvider } from "@catchoftheday/customer";
import {
  CancellationModalContainer,
  LinkingModalContainer,
  MigrationModalContainer,
  UpsellModalContainer,
} from "@catchoftheday/onepass";
import { FreeShippingDrawer } from "@catchoftheday/personalisation";
import { ThemeProvider } from "@catchoftheday/theme";
import { hideOnAndroidWebView } from "@catchoftheday/utilities";

import { CouponNotification } from "@header/components/CouponNotification";
import { FlashMessageBanner } from "@header/components/FlashMessageBanner";
import { GlobalAlertBanner } from "@header/components/GlobalAlertBanner/GlobalAlertBanner";
import { MegaMenu } from "@header/components/MegaMenu";
import { OnePassNotification } from "@header/components/NotificationToast";
import { PrimaryHeader } from "@header/components/PrimaryHeader";
import theme from "@header/components/theme";
import { TopHeader } from "@header/components/TopHeader";
import { useHeaderConfigurationQuery } from "@header/generated/graphql";
import { HeaderConfiguration } from "@header/types";

import { HeaderContext, HeaderContextProps } from "../../context";
import { HeaderToastContainer } from "../HeaderToast";

import { useHeaderMetaData } from "./metadata";

enableES5();
setUseProxies(false);

export type EnhancedHeaderProps = HeaderContextProps & {
  host?: string;

  /** Hides the MiniCart item count badge.  */
  hideCartBadge?: boolean;

  /** Hides the mega menu.  */
  hideMegaMenu?: boolean;

  /** Ref to the sticky section of the header. */
  stickyRef?: React.Ref<HTMLDivElement>;
};

const EnhancedHeaderInternal = ({
  onSearchChange,
  host = "",
  hideCartBadge,
  hideMegaMenu,
  stickyRef,
}: EnhancedHeaderProps) => {
  const { data } = useHeaderConfigurationQuery({
    variables: { domain: host },
  });

  const headerConfiguration = data?.configuration.header as HeaderConfiguration;
  const { metaData, initialHeaderMetaData, isLoading } =
    useHeaderMetaData(headerConfiguration);

  const cartItemCount = hideCartBadge ? undefined : metaData?.cartItemCount;

  return (
    <HeaderContext.Provider value={{ onSearchChange }}>
      <CustomerContextProvider>
        <ThemeProvider
          theme={theme}
          enableGlobalFontConfig={false}
          enableGlobalBoxSizingConfig
        >
          <Box id="top-header">
            {!isLoading &&
              metaData?.loggedIn &&
              metaData.couponNotificationEnabled && (
                <CouponNotification displayName={metaData?.displayName} />
              )}
            <Hide xs sm>
              <TopHeader
                isLoggedIn={metaData?.loggedIn}
                headerConfiguration={headerConfiguration}
              />
            </Hide>
          </Box>
          <Box ref={stickyRef} position="sticky" top={0} zIndex={3}>
            <PrimaryHeader
              id="primary-header"
              isLoading={isLoading}
              isLoggedIn={metaData?.loggedIn}
              isCCMember={metaData?.clubActive}
              isFlybuysLinked={metaData?.flybuysLinked}
              userName={metaData?.displayName}
              cartItemsAmount={cartItemCount}
              wishItemsAmount={metaData?.wishlistItemCount}
              myAccountMessageCount={metaData?.messageCount}
              headerConfiguration={headerConfiguration}
            />

            {!hideMegaMenu && (
              <Box height={{ xs: "auto", md: "50px" }}>
                {initialHeaderMetaData?.megaMenuEnabled &&
                  initialHeaderMetaData?.navData && (
                    <MegaMenu
                      id="mega-menu"
                      shops={initialHeaderMetaData.navData}
                      essentialsMenu={initialHeaderMetaData.essentialsMenu}
                      accountMenu={{
                        ...initialHeaderMetaData.accountMenu,
                        flybuysNotificationCount:
                          metaData?.flybuysNotificationCount ?? 0,
                        unreadMarketplaceMessageCount:
                          metaData?.unreadMarketplaceMessageCount ?? 0,
                      }}
                      cartItemsCount={cartItemCount}
                      wishlistItemsCount={metaData?.wishlistItemCount}
                      isLoggedIn={metaData?.loggedIn}
                    />
                  )}
              </Box>
            )}
            <FlashMessageBanner isMicrofrontend />
            <GlobalAlertBanner />
            <HeaderToastContainer />
          </Box>
          <FreeShippingDrawer />
          <LinkingModalContainer />
          <MigrationModalContainer />
          <CancellationModalContainer />
          <UpsellModalContainer />
          <OnePassNotification />
        </ThemeProvider>
      </CustomerContextProvider>
    </HeaderContext.Provider>
  );
};

export const EnhancedHeader = hideOnAndroidWebView(EnhancedHeaderInternal);
